import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { BatteryInfoResponse } from '@/features/resource/endpoints/resources'
import { GET_BATTERY_INFO_API_ID, getBatteryInfo } from '@/features/resource/endpoints/resources'

export type UseBatteryInfoQueryResult = Omit<UseQueryResult, 'data'> & {
  batteryInfo?: BatteryInfoResponse
}

/**
 * Read-only battery telemetry (state of charge, rated energy, etc.) for a single control port.
 *
 * The polling variant `useBatteryInfoWithPollingQuery` exists for cards that need live updates;
 * this hook is the non-polling counterpart used by surfaces that just want the latest snapshot
 * once (e.g. the operating-envelope chart's energy axis).
 *
 * Disabled when `controlPortId` is `null` — consumers that don't have a battery port (CLOUD
 * resources, non-battery resources) can pass `null` and the query never fires.
 */
export function useBatteryInfoQuery(controlPortId: string | null): UseBatteryInfoQueryResult {
  const enabled = controlPortId !== null
  const { data, ...queryResult } = useQuery({
    enabled,
    // Keep the literal null in the cache key (rather than substituting an empty string) so the
    // disabled-query slot can't ever collide with a real fetch.
    queryKey: [GET_BATTERY_INFO_API_ID, { controlPortId }],
    queryFn: () => getBatteryInfo(controlPortId ?? ''),
  })

  useErrorHandler(queryResult.isError)

  return { batteryInfo: data, ...queryResult }
}

/**
 * Companion invalidator — call after a mutation that may have updated the battery's telemetry
 * (rated energy, control-port assignment, etc.) to refetch on next access. Mirrors the pattern
 * used by `useInvalidateResourceJsonQuery`.
 */
export function useInvalidateBatteryInfoQuery() {
  const queryClient = useQueryClient()

  return (controlPortId: string | null) =>
    queryClient.invalidateQueries({
      queryKey: [GET_BATTERY_INFO_API_ID, { controlPortId }],
    })
}
